/**
 * Página de prueba para BioTemplatePremium (producción).
 * Acceso protegido: requiere ?key=anaafernandez-dev-2024
 * Permite alternar entre Argentina (ARS) e Internacional (USD).
 */

import { BRAND_PERSON_NAME } from "@/lib/brandConfig";
import BioTemplatePremium from "@/components/templates/BioTemplatePremium";
import {
  getCurrencyForCountry,
  getPlansForCurrency,
} from "@/config/bioTemplatePremiumPlans";
import {
  getPremiumInvitePaths,
  PREMIUM_INVITE_FLOW_NAME,
} from "@/config/bioTemplatePremiumFlow";
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

type TestMode = "AR" | "US";

const DEV_KEY = "anaafernandez-dev-2024";

export default function TestNewTemplate() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [mode, setMode] = useState<TestMode>("AR");
  const [showSelector, setShowSelector] = useState(true);

  useEffect(() => {
    const key = searchParams.get("key");
    if (key === DEV_KEY) {
      setAuthorized(true);
    } else {
      navigate("/", { replace: true });
    }
  }, [searchParams, navigate]);

  const country = mode;
  const language = mode === "AR" ? "es-AR" : "en-US";
  const currency = getCurrencyForCountry(country);
  const { standardPlan, vipPlan } = getPlansForCurrency(currency, language);
  const { successPath, cancelPath } = getPremiumInvitePaths(currency);

  if (!authorized) {
    return null;
  }

  if (showSelector) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-8 p-4">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold">Test BioTemplatePremium</h1>
          <p className="text-gray-400 text-sm">Seleccioná el modo para probar</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => { setMode("AR"); setShowSelector(false); }}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl font-bold text-white text-lg hover:opacity-90 transition-opacity"
          >
            🇦🇷 Argentina (ARS)
            <span className="block text-xs font-normal opacity-70 mt-1">MercadoPago Subscription</span>
          </button>

          <button
            onClick={() => { setMode("US"); setShowSelector(false); }}
            className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-bold text-white text-lg hover:opacity-90 transition-opacity"
          >
            🌎 Internacional (USD)
            <span className="block text-xs font-normal opacity-70 mt-1">Stripe Subscription Checkout</span>
          </button>
        </div>

        <div className="text-center text-gray-500 text-xs space-y-1 mt-4">
          <p>Standard: {currency === "ARS" ? `$${standardPlan.price.toLocaleString()} ARS` : `$${standardPlan.price} USD`}</p>
          <p>VIP: {currency === "ARS" ? `$${vipPlan.price.toLocaleString()} ARS` : `$${vipPlan.price} USD`}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Botón flotante para volver al selector */}
      <button
        onClick={() => setShowSelector(true)}
        className="fixed top-4 right-4 z-[100] px-3 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white text-xs font-medium hover:bg-white/20 transition-colors"
      >
        ← Cambiar modo ({mode === "AR" ? "🇦🇷 AR" : "🌎 US"})
      </button>

      <BioTemplatePremium
        currency={currency}
        country={country}
        language={language}
        standardPlan={standardPlan}
        vipPlan={vipPlan}
        successPath={successPath}
        cancelPath={cancelPath}
        profileName={BRAND_PERSON_NAME}
        flowName={PREMIUM_INVITE_FLOW_NAME}
      />
    </>
  );
}
